import React, { Suspense, lazy, memo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import SessionExpiredModal from './components/SessionExpiredModal';
import MarketingCampaignPopup from './components/MarketingCampaignPopup';
import './App.css';
import './mobile/styles/mobile.css';

// Lightweight loading spinner for lazy loaded components
const PageLoader = memo(() => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-[#1a365d] border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
));

// Critical pages - load immediately
import LandingPage from './pages/LandingPage';
import MoringaInvestPage from './pages/MoringaInvestPage';
import MoringaDashboardPage from './pages/MoringaDashboardPage';
import { LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';

// Lazy loaded pages - load on demand
const ApplicationSubmittedPage = lazy(() => import('./pages/ApplicationSubmittedPage').then(m => ({ default: m.ApplicationSubmittedPage })));
const ActivateAccountPage = lazy(() => import('./pages/ActivateAccountPage').then(m => ({ default: m.ActivateAccountPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const KYCWizardPage = lazy(() => import('./pages/KYCWizardPage'));
const BeneficiariesPage = lazy(() => import('./pages/BeneficiariesPage'));
const StatementsPage = lazy(() => import('./pages/StatementsPage'));
const PixWithdrawPage = lazy(() => import('./pages/PixWithdrawPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const BankingTransfersPage = lazy(() => import('./pages/TransfersPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const DepositPage = lazy(() => import('./pages/DepositPage'));
const InternalTransferPage = lazy(() => import('./pages/InternalTransferPage'));
const AccountSetupPage = lazy(() => import('./pages/AccountSetupPage'));
const CryptoSwapPage = lazy(() => import('./pages/CryptoSwapPage'));
const CryptoOpportunityPage = lazy(() => import('./pages/CryptoOpportunityPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const AdminUSDTzPage = lazy(() => import('./pages/AdminUSDTzPage'));
const AdminMoringaPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminMoringaPage })));

// Lazy load transfer pages
const TransfersPages = lazy(() => import('./pages/TransfersPages'));
const UserTransfersPage = lazy(() => import('./pages/TransfersPages').then(m => ({ default: m.TransfersPage })));
const NewTransferPage = lazy(() => import('./pages/TransfersPages').then(m => ({ default: m.NewTransferPage })));
const TransferDetailPage = lazy(() => import('./pages/TransfersPages').then(m => ({ default: m.TransferDetailPage })));

// Lazy load static pages
const AboutUsPage = lazy(() => import('./pages/AboutUsPage').then(m => ({ default: m.AboutUsPage })));
const CareersPage = lazy(() => import('./pages/CareersPage').then(m => ({ default: m.CareersPage })));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const COApplicationPage = lazy(() => import('./pages/COApplicationPage').then(m => ({ default: m.COApplicationPage })));

// Lazy load admin pages (heavy)
const AdminOverviewPage = lazy(() => import('./pages/AdminPages').then(m => ({ default: m.AdminOverviewPage })));
const AdminUsersPage = lazy(() => import('./pages/AdminPages').then(m => ({ default: m.AdminUsersPage })));
const AdminUserDetailPage = lazy(() => import('./pages/AdminPages').then(m => ({ default: m.AdminUserDetailPage })));

const AdminKYCPage = lazy(() => import('./pages/AdminSubPages').then(m => ({ default: m.AdminKYCPage })));
const AdminTransfersPage = lazy(() => import('./pages/AdminSubPages').then(m => ({ default: m.AdminTransfersPage })));
const AdminLimitsPage = lazy(() => import('./pages/AdminSubPages').then(m => ({ default: m.AdminLimitsPage })));
const AdminRiskRulesPage = lazy(() => import('./pages/AdminSubPages').then(m => ({ default: m.AdminRiskRulesPage })));
const AdminAuditPage = lazy(() => import('./pages/AdminSubPages').then(m => ({ default: m.AdminAuditPage })));
const AdminTicketsPage = lazy(() => import('./pages/AdminSubPages').then(m => ({ default: m.AdminTicketsPage })));
const AdminEmailDeliveryPage = lazy(() => import('./pages/AdminEmailDeliveryPage'));
const AdminEmailSettingsPage = lazy(() => import('./pages/AdminSubPages').then(m => ({ default: m.AdminEmailSettingsPage })));

const AdminPlatformFeesPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminPlatformFeesPage })));
const AdminCommercialOfficersPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminCommercialOfficersPage })));
const AdminCryptoDepositsPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminCryptoDepositsPage })));
const AdminCommissionsPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminCommissionsPage })));
const AdminFiatDepositsPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminFiatDepositsPage })));
const AdminCOApplicationsPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminCOApplicationsPage })));
const AdminCryptoDeskPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminCryptoDeskPage })));
const AdminSetupPaymentsPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminSetupPaymentsPage })));
const AdminCryptoSwapsPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminCryptoSwapsPage })));
const AdminClientWithdrawalsPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminClientWithdrawalsPage })));
const AdminProductsPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminProductsPage })));
const AdminAccountUpgradesPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminAccountUpgradesPage })));
const AdminInfinitePayPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminInfinitePayPage })));
const AdminMarketingPage = lazy(() => import('./pages/AdminNewPages').then(m => ({ default: m.AdminMarketingPage })));

// Lazy load CO pages
const CODashboardPage = lazy(() => import('./pages/COPages').then(m => ({ default: m.CODashboardPage })));
const COClientsPage = lazy(() => import('./pages/COPages').then(m => ({ default: m.COClientsPage })));
const COCommissionsPage = lazy(() => import('./pages/COPages').then(m => ({ default: m.COCommissionsPage })));
const COProfilePage = lazy(() => import('./pages/COPages').then(m => ({ default: m.COProfilePage })));
const COSettingsPage = lazy(() => import('./pages/COPages').then(m => ({ default: m.COSettingsPage })));
const COCryptoSwapPage = lazy(() => import('./pages/COPages').then(m => ({ default: m.COCryptoSwapPage })));

// Mobile PWA Pages
const SplashScreen = lazy(() => import('./mobile/pages/SplashScreen'));
const OnboardingScreen = lazy(() => import('./mobile/pages/OnboardingScreen'));
const MobileLoginPage = lazy(() => import('./mobile/pages/MobileLoginPage'));
const MobileSignupPage = lazy(() => import('./mobile/pages/MobileSignupPage'));
const MobileForgotPasswordPage = lazy(() => import('./mobile/pages/MobileForgotPasswordPage'));
const MobileDashboard = lazy(() => import('./mobile/pages/MobileDashboard'));
const MobileWalletsPage = lazy(() => import('./mobile/pages/MobileWalletsPage'));
const MobileTransferPage = lazy(() => import('./mobile/pages/MobileTransferPage'));
const MobileActivityPage = lazy(() => import('./mobile/pages/MobileActivityPage'));
const MobileProfilePage = lazy(() => import('./mobile/pages/MobileProfilePage'));
const MobileDepositPage = lazy(() => import('./mobile/pages/MobileDepositPage'));
const MobileConvertPage = lazy(() => import('./mobile/pages/MobileConvertPage'));
const MobileProductsPage = lazy(() => import('./mobile/pages/MobileProductsPage'));
const MobileNotificationsPage = lazy(() => import('./mobile/pages/MobileNotificationsPage'));
const MobileBankDetailsPage = lazy(() => import('./mobile/pages/MobileBankDetailsPage'));
const MobileKYCPage = lazy(() => import('./mobile/pages/MobileKYCPage'));
const MobileSecurityPage = lazy(() => import('./mobile/pages/MobileSecurityPage'));
const MobileSupportPage = lazy(() => import('./mobile/pages/MobileSupportPage'));
const MobileDownloadPage = lazy(() => import('./mobile/pages/MobileDownloadPage'));
const MobileSwapPage = lazy(() => import('./mobile/pages/MobileSwapPage'));
const MobileCryptoDetailPage = lazy(() => import('./mobile/pages/MobileCryptoDetailPage'));
const MobileCryptoWithdrawPage = lazy(() => import('./mobile/pages/MobileCryptoWithdrawPage'));
const AccountUpgradePage = lazy(() => import('./pages/AccountUpgradePage'));

// Lightweight global components (NOT lazy so they don't pop-late)

// Protected Route Component - Memoized for performance
const ProtectedRoute = memo(({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return (
    <>
      {children}
      <MarketingCampaignPopup />
    </>
  );
});

// Public Route - Memoized
const PublicRoute = memo(({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (isAuthenticated) {
    if (user?.role === 'ADMIN' || user?.role === 'COMPLIANCE') {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === 'COMMERCIAL_OFFICER') {
      return <Navigate to="/co" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
});

// Admin Route - Memoized
const AdminRoute = memo(({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'ADMIN' && user?.role !== 'COMPLIANCE') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
});

// CO Route - Memoized
const CORoute = memo(({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'COMMERCIAL_OFFICER') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <>
      {children}
      <MarketingCampaignPopup />
    </>
  );
});

// Mobile Protected Route
const MobileProtectedRoute = memo(({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="m-app">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100dvh' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid rgba(59,130,246,0.3)', borderTop: '3px solid #3B82F6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/m/login" replace />;
  }
  
  return (
    <>
      {children}
      <MarketingCampaignPopup />
    </>
  );
});

// Mobile Public Route
const MobilePublicRoute = memo(({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) return null;
  
  if (isAuthenticated) {
    return <Navigate to="/m/dashboard" replace />;
  }
  
  return children;
});

// Main App Component
function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes - Critical (not lazy loaded) */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                
                {/* Public Routes - Lazy Loaded */}
                <Route path="/co-application" element={<COApplicationPage />} />
                <Route path="/moringa-invest" element={<MoringaInvestPage />} />
                <Route path="/application-submitted" element={<ApplicationSubmittedPage />} />
                <Route path="/activate" element={<ActivateAccountPage />} />
                
                {/* Protected User Routes */}
                <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/moringa-dashboard" element={<ProtectedRoute><MoringaDashboardPage /></ProtectedRoute>} />
                <Route path="/kyc" element={<ProtectedRoute><KYCWizardPage /></ProtectedRoute>} />
                <Route path="/transfers" element={<ProtectedRoute><UserTransfersPage /></ProtectedRoute>} />
                <Route path="/transfers/new" element={<ProtectedRoute><NewTransferPage /></ProtectedRoute>} />
                <Route path="/transfers/:id" element={<ProtectedRoute><TransferDetailPage /></ProtectedRoute>} />
                <Route path="/beneficiaries" element={<ProtectedRoute><BeneficiariesPage /></ProtectedRoute>} />
                <Route path="/statements" element={<ProtectedRoute><StatementsPage /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="/account-upgrade" element={<ProtectedRoute><AccountUpgradePage /></ProtectedRoute>} />
                <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
                <Route path="/banking-transfers" element={<ProtectedRoute><BankingTransfersPage /></ProtectedRoute>} />
                <Route path="/deposit" element={<ProtectedRoute><DepositPage /></ProtectedRoute>} />
                <Route path="/withdraw/pix" element={<ProtectedRoute><PixWithdrawPage /></ProtectedRoute>} />
                <Route path="/internal-transfer" element={<ProtectedRoute><InternalTransferPage /></ProtectedRoute>} />
                <Route path="/account-setup" element={<ProtectedRoute><AccountSetupPage /></ProtectedRoute>} />
                <Route path="/crypto-swap" element={<ProtectedRoute><CryptoSwapPage /></ProtectedRoute>} />
                <Route path="/crypto-opportunity" element={<ProtectedRoute><CryptoOpportunityPage /></ProtectedRoute>} />
                <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminRoute><AdminOverviewPage /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
                <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
                <Route path="/admin/kyc" element={<AdminRoute><AdminKYCPage /></AdminRoute>} />
                <Route path="/admin/transfers" element={<AdminRoute><AdminTransfersPage /></AdminRoute>} />
                <Route path="/admin/limits" element={<AdminRoute><AdminLimitsPage /></AdminRoute>} />
                <Route path="/admin/risk-rules" element={<AdminRoute><AdminRiskRulesPage /></AdminRoute>} />
                <Route path="/admin/audit" element={<AdminRoute><AdminAuditPage /></AdminRoute>} />
                <Route path="/admin/tickets" element={<AdminRoute><AdminTicketsPage /></AdminRoute>} />
                <Route path="/admin/email-settings" element={<AdminRoute><AdminEmailSettingsPage /></AdminRoute>} />
                <Route path="/admin/email-delivery" element={<AdminRoute><AdminEmailDeliveryPage /></AdminRoute>} />
                <Route path="/admin/platform-fees" element={<AdminRoute><AdminPlatformFeesPage /></AdminRoute>} />
                <Route path="/admin/commercial-officers" element={<AdminRoute><AdminCommercialOfficersPage /></AdminRoute>} />
                <Route path="/admin/crypto-deposits" element={<AdminRoute><AdminCryptoDepositsPage /></AdminRoute>} />
                <Route path="/admin/commissions" element={<AdminRoute><AdminCommissionsPage /></AdminRoute>} />
                <Route path="/admin/fiat-deposits" element={<AdminRoute><AdminFiatDepositsPage /></AdminRoute>} />
                <Route path="/admin/co-applications" element={<AdminRoute><AdminCOApplicationsPage /></AdminRoute>} />
                <Route path="/admin/crypto-desk" element={<AdminRoute><AdminCryptoDeskPage /></AdminRoute>} />
                <Route path="/admin/setup-payments" element={<AdminRoute><AdminSetupPaymentsPage /></AdminRoute>} />
                <Route path="/admin/co-swaps" element={<AdminRoute><AdminCryptoSwapsPage /></AdminRoute>} />
                <Route path="/admin/client-withdrawals" element={<AdminRoute><AdminClientWithdrawalsPage /></AdminRoute>} />
                <Route path="/admin/products" element={<AdminRoute><AdminProductsPage /></AdminRoute>} />
                <Route path="/admin/account-upgrades" element={<AdminRoute><AdminAccountUpgradesPage /></AdminRoute>} />
                <Route path="/admin/infinitepay" element={<AdminRoute><AdminInfinitePayPage /></AdminRoute>} />
                <Route path="/admin/marketing" element={<AdminRoute><AdminMarketingPage /></AdminRoute>} />
                <Route path="/admin/moringa" element={<AdminRoute><AdminMoringaPage /></AdminRoute>} />
                <Route path="/admin/treasury/usdtz" element={<AdminRoute><AdminUSDTzPage /></AdminRoute>} />
                
                {/* CO Routes */}
                <Route path="/co" element={<CORoute><CODashboardPage /></CORoute>} />
                <Route path="/co/clients" element={<CORoute><COClientsPage /></CORoute>} />
                <Route path="/co/commissions" element={<CORoute><COCommissionsPage /></CORoute>} />
                <Route path="/co/profile" element={<CORoute><COProfilePage /></CORoute>} />
                <Route path="/co/settings" element={<CORoute><COSettingsPage /></CORoute>} />
                <Route path="/co/crypto-swap" element={<CORoute><COCryptoSwapPage /></CORoute>} />
                
                {/* Static Pages */}
                <Route path="/about" element={<AboutUsPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/payment-success" element={<PaymentSuccessPage />} />
                
                {/* Mobile PWA Routes */}
                <Route path="/m" element={<SplashScreen />} />
                <Route path="/m/onboarding" element={<OnboardingScreen />} />
                <Route path="/m/login" element={<MobilePublicRoute><MobileLoginPage /></MobilePublicRoute>} />
                <Route path="/m/signup" element={<MobilePublicRoute><MobileSignupPage /></MobilePublicRoute>} />
                <Route path="/m/forgot-password" element={<MobileForgotPasswordPage />} />
                <Route path="/m/dashboard" element={<MobileProtectedRoute><MobileDashboard /></MobileProtectedRoute>} />
                <Route path="/m/wallets" element={<MobileProtectedRoute><MobileWalletsPage /></MobileProtectedRoute>} />
                <Route path="/m/transfer" element={<MobileProtectedRoute><MobileTransferPage /></MobileProtectedRoute>} />
                <Route path="/m/activity" element={<MobileProtectedRoute><MobileActivityPage /></MobileProtectedRoute>} />
                <Route path="/m/profile" element={<MobileProtectedRoute><MobileProfilePage /></MobileProtectedRoute>} />
                <Route path="/m/deposit" element={<MobileProtectedRoute><MobileDepositPage /></MobileProtectedRoute>} />
                <Route path="/m/convert" element={<MobileProtectedRoute><MobileConvertPage /></MobileProtectedRoute>} />
                <Route path="/m/products" element={<MobileProtectedRoute><MobileProductsPage /></MobileProtectedRoute>} />
                <Route path="/m/notifications" element={<MobileProtectedRoute><MobileNotificationsPage /></MobileProtectedRoute>} />
                <Route path="/m/bank-details" element={<MobileProtectedRoute><MobileBankDetailsPage /></MobileProtectedRoute>} />
                <Route path="/m/kyc" element={<MobileProtectedRoute><MobileKYCPage /></MobileProtectedRoute>} />
                <Route path="/m/security" element={<MobileProtectedRoute><MobileSecurityPage /></MobileProtectedRoute>} />
                <Route path="/m/support" element={<MobileProtectedRoute><MobileSupportPage /></MobileProtectedRoute>} />
                <Route path="/m/swap" element={<MobileProtectedRoute><MobileSwapPage /></MobileProtectedRoute>} />
                <Route path="/m/crypto/:symbol" element={<MobileProtectedRoute><MobileCryptoDetailPage /></MobileProtectedRoute>} />
                <Route path="/m/crypto-withdraw" element={<MobileProtectedRoute><MobileCryptoWithdrawPage /></MobileProtectedRoute>} />
                <Route path="/m/account-upgrade" element={<MobileProtectedRoute><AccountUpgradePage /></MobileProtectedRoute>} />
                <Route path="/m/download" element={<MobileDownloadPage />} />
                <Route path="/download" element={<MobileDownloadPage />} />
                
                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            <SessionExpiredModal />
            <Toaster position="top-right" richColors closeButton />
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
